import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { ExternalLink } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { VersionPickerModal } from '@/components/AssortmentPlansGrid/VersionPickerModal'
import { useCurrentVersionStore } from '@/stores/useCurrentVersionStore'
import type { AssortmentPlan } from '@/types'

// Icon link to open a plan; disabled when the current user has no access to it
export function PlanLinkCell(props: CustomCellRendererProps<AssortmentPlan>) {
  const plan = props.data
  const navigate = useNavigate()
  const clearCurrentVersion = useCurrentVersionStore(s => s.clearCurrentVersion)
  const [showVersionPicker, setShowVersionPicker] = useState(false)

  if (!plan) return null

  const handleOpen = () => {
    if (plan.versions_count > 1) {
      setShowVersionPicker(true)
    } else {
      clearCurrentVersion(plan.id)
      navigate(`/plans/${plan.id}`)
    }
  }

  return (
    <TooltipProvider>
      <div className="flex h-full items-center justify-center">
        <Tooltip>
          <TooltipTrigger asChild>
            {/* Wrapper keeps the tooltip working when the button is disabled */}
            <span tabIndex={plan.can_access ? -1 : 0}>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 disabled:cursor-not-allowed"
                onClick={handleOpen}
                disabled={!plan.can_access}
                aria-label={`Open plan ${plan.name}`}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {plan.can_access
                ? 'Open plan'
                : "You don't have access to this plan"}
            </p>
          </TooltipContent>
        </Tooltip>
      </div>

      {showVersionPicker && (
        <VersionPickerModal
          open={showVersionPicker}
          onOpenChange={setShowVersionPicker}
          planId={plan.id}
          planName={plan.name}
        />
      )}
    </TooltipProvider>
  )
}
